<template>
  <v-main>

    <v-container
        id="invoice-container"
        fluid
        tag="section"
    >

      <base-material-card
          icon="mdi-text-box-edit-outline"
          class="px-5 py-3"
      >

        <shared-page-messages :error-messages="errorMessages"
                              :warning-messages="warningMessages"
                              :success-messages="successMessages">
        </shared-page-messages>

        <v-form ref="invoiceForm"
                v-model="valid"
                @submit.prevent="validate"
                lazy-validation
        >

          <v-row class="mt-5" v-if="invoice.user">
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Bill To
            </v-col>
            <v-col
                cols="9"
                sm="9"
            >
              {{ invoice.user.names }}
            </v-col>
          </v-row>

          <v-row class="mt-5" v-if="invoice.claim && invoice.claim.user && invoice.claim.dependent">
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Claim
            </v-col>
            <v-col
                cols="9"
                sm="9"
            >
              {{ invoice.claim.user.names }} for {{ invoice.claim.dependent.names }}
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Invoice Date
            </v-col>

            <v-col
                cols="3"
                sm="3"
            >
              <v-text-field
                  v-model="computedInvoiceDate"
                  type="date"
                  :rules="rules.date"
                  class="mt-0 pt-0"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Due Date
            </v-col>
            <v-col
                cols="3"
                sm="3"
            >
              <v-text-field
                  v-model="computedDueDate"
                  type="date"
                  :rules="rules.date"
                  class="mt-0 pt-0"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Status
            </v-col>

            <v-col
                cols="12"
                sm="3"
            >
              <v-select
                  v-model="form.status"
                  label="Status"
                  :items="computedInvoiceStatusItems"
                  :rules="rules.required"
                  class="mt-0 pt-0"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="3"
                sm="3"
                class="text-subtitle-2"
            >
              Comments
            </v-col>

            <v-col
                cols="12"
                sm="6"
            >
              <v-text-field
                  v-model="form.comments"
                  type="text"
                  class="mt-0 pt-0"
              ></v-text-field>
            </v-col>

          </v-row>


          <v-row>
            <v-col
                cols="6"
                sm="3"
            >
              <div class="my-2">
                <v-btn
                    :disabled="processing"
                    :loading="processing"
                    color="primary"
                    dark
                    type="submit"
                >
                  Submit
                </v-btn>
              </div>
            </v-col>
          </v-row>

        </v-form>


      </base-material-card>

      <v-expansion-panels
          multiple
          light
          hover
          v-model="expandedPanels"
          v-if="invoice.id"
      >
        <v-expansion-panel>
          <v-expansion-panel-header>Audit Trail</v-expansion-panel-header>

          <v-expansion-panel-content>
            <shared-audit-trail-view :item-id="invoiceId"></shared-audit-trail-view>
          </v-expansion-panel-content>

        </v-expansion-panel>
      </v-expansion-panels>

    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import validation from "@/utils/validation"
import moment from "moment";
import constants from "@/utils/constants";
import SharedPageMessages from '../shared/PageMessages.vue'
import SharedAuditTrailView from '../shared/AuditTrailView.vue'

export default {
  name: 'CoreEditInvoiceView',
  components: {SharedAuditTrailView, SharedPageMessages},

  async created() {
    this.invoiceId = this.$route.params.invoiceId
    if (this.invoiceId !== null && this.invoiceId !== undefined) {
      await this.fetchInvoice(this.invoiceId)
    }
  },

  computed: {
    computedInvoiceStatusItems: {
      get: function () {
        return Object.keys(this.invoiceStatusMap)
            .map((key) => ({value: key, text: this.invoiceStatusMap[key]}))
      }
    },
    computedInvoiceDate: {
      get() {
        if (this.form.invoiceDate == null) {
          return
        }
        return moment(this.form.invoiceDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      },
      set(value) {
        this.form.invoiceDate = value
      }
    },
    computedDueDate: {
      get() {
        if (this.form.dueDate == null) {
          return
        }
        return moment(this.form.dueDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      },
      set(value) {
        this.form.dueDate = value
      }
    }
  },

  methods: {
    clearMessages() {
      this.errorMessages = []
      this.warningMessages = []
      this.successMessages = []
    },
    validate() {
      if(this.$refs.invoiceForm.validate()) {
        this.updateInvoice()
      }
    },

    async fetchInvoice(invoiceId) {
      this.clearMessages()
      return api.fetchInvoice(invoiceId)
          .then(response => {
            this.invoice = response.data
            this.buildForm(this.invoice)
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    async buildForm(invoice) {
      this.form = {
        invoiceDate: invoice.invoiceDate ? moment(invoice.invoiceDate).format('YYYY-MM-DD') : null,
        dueDate: invoice.dueDate ? moment(invoice.dueDate).format('YYYY-MM-DD') : null,
        status: invoice.status,
        comments: invoice.comments
      }
    },

    buildPayload() {
      return {
        dueDate: this.form.dueDate,
        invoiceDate: this.form.invoiceDate,
        status: this.form.status,
        comments: this.form.comments,
      }
    },

    async updateInvoice() {
      this.processing = true
      const payload = this.buildPayload()

      return api.updateInvoice(this.invoice.user.id, this.invoice.id, payload)
          .then(response => {
            this.invoice = response.data
            this.$router.push(`/invoices/${this.invoice.id}/view`)
          })
          .catch(err => {
            this.handleError(err)
          })
          .finally(() => {
            this.processing = false
          })
    },
    handleError(err) {
      let errorMessage = api.parseResponseError(err)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      valid: true,
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      invoiceId: null,
      form: {},
      invoice: {},

      invoiceStatusMap: constants.getInvoiceStatusMap(),
      processing: false,
      expandedPanels: [0],

      rules: {
        required: [
          v => validation.isRequired(v) || 'is required'
        ],
        date: [
            v => validation.isDate(v) || 'must be a valid date'
        ]
      }
    }
  }
}
</script>
